import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { contextQuery } from '../api/jedi'
import { useEngagement } from '../lib/engagement'
import { LoadProblem } from '../shell/LoadProblem'
import { RecordSurface } from '../shell/RecordSurface'
import { readContextOf } from '../shell/readRecord'
import { recordSearch, useLinkedRecord } from '../shell/recordLink'

export const Route = createFileRoute('/_shell/context')({
  component: ContextView,
  // A cited record's door (wave 23): the chat's record chip lands here.
  validateSearch: recordSearch,
})

/**
 * Engagement Context at wave-3 step-5 fidelity: the standing conditions —
 * definitions, guiding principles, guardrails, constraints — as a minimal
 * read list off the context lens. READ-ONLY BY CONSTRUCTION (C-033), and the
 * 2-Aug ruling that entries can be added "directly on the Context surface"
 * means exactly this surface's chat door (the record panel's door, the rail
 * chat) — never an inline editor; stated here per the wave-3 call-2 rider
 * rather than left to drift. The designed drill-down conversion stays with
 * the design lane.
 */
function ContextView() {
  const { id: engagementId } = useEngagement()
  const entries = useQuery(contextQuery(engagementId))
  const [selected, setSelected] = useState<{ id: string; title: string } | null>(null)

  const select = (id: string, title: string) =>
    setSelected((current) => (current?.id === id ? null : { id, title }))
  const { record } = Route.useSearch()
  useLinkedRecord(record, entries.data, (entry) => entry.id,
    (entry) => setSelected({ id: entry.id, title: entry.title }))
  const selectedEntry = (entries.data ?? []).find((e) => e.id === selected?.id) ?? null

  return (
    <section className="surface surface-wide">
      <header className="surface-head">
        <h1>Context</h1>
        <p className="surface-sub">
          The standing conditions this engagement runs under. They never resolve; they shape
          decisions.
        </p>
      </header>

      {entries.isPending && <p className="surface-note">Loading…</p>}
      <LoadProblem error={entries.error} />
      {entries.data?.length === 0 && (
        <p className="surface-note">
          No standing conditions yet. State one in the chat and approve it.
        </p>
      )}

      <div className="work-layout">
        <div className="work-main">
          <ul className="ledger-list">
            {entries.data?.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  className={
                    selected?.id === entry.id ? 'ledger-row ledger-row-selected' : 'ledger-row'
                  }
                  onClick={() => select(entry.id, entry.title)}
                >
                  <div className="ledger-row-main">
                    <span className="ledger-type">{entry.kind.replace('_', ' ')}</span>
                    <span className="ledger-row-title">{entry.title}</span>
                  </div>
                  <div className="ledger-row-tags">
                    <span className="plan-tag">{entry.audience}</span>
                  </div>
                </button>
                {selected?.id === entry.id && entry.statement && (
                  <p className="context-statement">{entry.statement}</p>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/*
          The `read` body (wave 24). The entry's own statement is in the first
          block and what it DOES is in the second, which is the half a reader
          cannot get from the list: a constraint and a guardrail are checked
          when a change is drafted, and a definition is not.
        */}
        {selectedEntry && (
          <RecordSurface
            record={readContextOf(selectedEntry)}
            backLabel="Context"
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </section>
  )
}
